import type { ProductType } from '../../types';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

type CartType = {
  total: number;
  payAmount: number;
  delivery: number;
  discount: number;
  subtotal: number;
  promoCode: string;
  promoAmount: number;
  list: ProductType[];
  discountAmount: number;
};

const initialState: CartType = {
  total: 0,
  list: [],
  payAmount: 0,
  delivery: 0,
  discount: 0,
  subtotal: 0,
  promoCode: '',
  promoAmount: 0,
  discountAmount: 0,
};

type StateType = typeof initialState;

/**
 * Helper function for updating totals
 */
function updateTotals(
  state: StateType,
  product: ProductType,
  quantity: number,
  add: boolean
) {
  const sign = add ? 1 : -1;

  if (product.off_price) {
    state.payAmount += sign * (product.off_price * quantity);
    state.discountAmount += sign * ((product.price * quantity) - (product.off_price * quantity));
  } else {
    state.payAmount += sign * (product.price * quantity);
  }

  state.total += sign * (product.price * quantity);
}

export const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    addToCart: (state, action: PayloadAction<ProductType>) => {
      const quantity = action.payload?.number_packaging ?? 1;
      const inCart = state.list.find(item => item.id === action.payload.id);

      if (inCart) {
        inCart.quantity = (inCart.quantity ?? 0) + quantity;
      } else {
        state.list.push({ ...action.payload, quantity });
      }

      updateTotals(state, action.payload, quantity, true);
    },

    removeFromCart: (state, action: PayloadAction<ProductType>) => {
      const quantity = action.payload?.number_packaging ?? 1;
      const index = state.list.findIndex(item => item.id === action.payload.id);

      if (index > -1) {
        const item = state.list[index];

        if ((item.quantity ?? 0) > quantity) {
          item.quantity = Math.max((item.quantity ?? 0) - quantity, 0);
        } else {
          state.list.splice(index, 1);
        }

        updateTotals(state, action.payload, quantity, false);
      }

      // Reset if cart is empty
      if (state.list.length === 0) {
        Object.assign(state, initialState);
      }
    },

    setDiscount: (state, action: PayloadAction<number>) => {
      state.discountAmount += action.payload;
      state.payAmount -= action.payload;
      state.promoAmount = action.payload;
    },

    resetCart: (state) => {
      Object.assign(state, initialState);
    },

    setPromoCode: (state, action: PayloadAction<string>) => {
      state.promoCode = action.payload;
    },
  },
});

export const {
  addToCart,
  resetCart,
  setDiscount,
  setPromoCode,
  removeFromCart
} = cartSlice.actions;

export default cartSlice.reducer;
